import React from "react";
import { nanoid } from "nanoid";
import { toast } from "react-toastify";

import Toast from "src/components/toast";
import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";
import { uploadPPTToS3 } from "src/services/experiences/importDesign";
import { useImportPPTMutation } from "src/query/experiences/importDesign";
import { BLANK_TEMPLATE_IDS } from "src/pages/experiences/constants/storyboard";
import {
  buildCleverstoryUrl,
  buildPageTargetPath,
} from "src/pages/experiences/utils/cleverstoryRedirect";

import { StoryboardsContext } from "../context";

export interface StartImportArgs {
  file: File;
  name: string;
  contentType: StoryboardType;
  /** Blob URL ownership transfers to the upload card (revoked in removeUpload). */
  thumbnailUrl: string | null;
  workspaceId: string;
}

const createUploadId = () => nanoid(6);

const showFailureToast = (message: string) => {
  toast(<Toast.Failure heading="Import failed" message={message} />, {
    autoClose: 4000,
  });
};

export function useStoryboardImport() {
  const { addUpload, updateUpload, removeUpload } =
    React.useContext(StoryboardsContext);
  const importMutation = useImportPPTMutation();

  const runImportInBackground = async (
    uploadId: string,
    pickedFile: File,
    trimmedName: string,
    storyboardTemplate: string,
    workspaceId: string,
  ) => {
    try {
      const { signedUrl } = await uploadPPTToS3(
        pickedFile,
        trimmedName,
        (percent) =>
          updateUpload(uploadId, {
            progress: Math.min(99, Math.round(percent)),
          }),
      );

      updateUpload(uploadId, { status: "processing", progress: 100 });

      const response = await importMutation.mutateAsync({
        name: trimmedName,
        signedUrl,
        storyboardTemplate,
      });

      removeUpload(uploadId);

      const newId = response?.storyboard?.page?.id;
      if (newId) {
        window
          .open(
            buildCleverstoryUrl(buildPageTargetPath(workspaceId, newId)),
            "_blank",
          )
          ?.focus();
      }

      toast(
        <Toast.Sucess
          message={`"${trimmedName}" was imported successfully.`}
        />,
        { autoClose: 3000 },
      );
    } catch (err: any) {
      removeUpload(uploadId);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while importing your PPT.";
      showFailureToast(message);
    }
  };

  const startImport = ({
    file,
    name,
    contentType,
    thumbnailUrl,
    workspaceId,
  }: StartImportArgs): boolean => {
    const storyboardTemplate = BLANK_TEMPLATE_IDS[contentType];
    if (!storyboardTemplate) {
      showFailureToast("This content type can't be imported from PPT.");
      return false;
    }

    const uploadId = createUploadId();

    addUpload({
      id: uploadId,
      name,
      contentType,
      thumbnailUrl,
      progress: 0,
      status: "uploading",
    });

    void runImportInBackground(
      uploadId,
      file,
      name,
      storyboardTemplate,
      workspaceId,
    );

    return true;
  };

  return { startImport };
}
